import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, KeenIcon } from '@/components';
import {
  Toolbar,
  ToolbarHeading,
  ToolbarPageTitle,
  ToolbarDescription,
  ToolbarActions,
} from '@/partials/toolbar';
import {
  useGetNotificationTemplateByIdQuery,
  useUpdateNotificationTemplateMutation,
} from '@/services/NotificationTemplateService';
import { Editor } from '@tinymce/tinymce-react';
import { TINYMCE_SCRIPT_SRC } from '@/constant';
import { NotificationTemplateResponse } from '@/types/notification-template-management.types';

const LANGS = ['en', 'fi', 'de', 'sv'] as const;

type Lang = (typeof LANGS)[number];

const NotificationTemplateDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const initialMode = (qs.get('mode') as 'view' | 'edit') || 'view';
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);

  const navState = location.state as {
    template?: NotificationTemplateResponse;
  } | null;
  const [template, setTemplate] = useState<NotificationTemplateResponse | null>(
    navState?.template || null
  );

  // If state not passed, fall back to loading EN to show meta (name) quickly
  const { data: enData } = useGetNotificationTemplateByIdQuery(
    { id: id || '', lang: 'en' },
    { skip: !id }
  );
  useEffect(() => {
    if (!template && enData?.data) {
      setTemplate(enData.data);
    }
  }, [enData, template]);

  // Fetch other languages to hydrate per-language fields
  const { data: fiData } = useGetNotificationTemplateByIdQuery(
    { id: id || '', lang: 'fi' },
    { skip: !id }
  );
  const { data: deData } = useGetNotificationTemplateByIdQuery(
    { id: id || '', lang: 'de' },
    { skip: !id }
  );
  const { data: svData } = useGetNotificationTemplateByIdQuery(
    { id: id || '', lang: 'sv' },
    { skip: !id }
  );

  const [titles, setTitles] = useState<Record<Lang, string>>({
    en: '',
    fi: '',
    de: '',
    sv: '',
  });
  const [descriptions, setDescriptions] = useState<Record<Lang, string>>({
    en: '',
    fi: '',
    de: '',
    sv: '',
  });

  useEffect(() => {
    const nextTitles: Record<Lang, string> = { en: '', fi: '', de: '', sv: '' };
    const nextDescriptions: Record<Lang, string> = {
      en: '',
      fi: '',
      de: '',
      sv: '',
    };

    if (enData?.data) {
      nextTitles.en = enData.data.title || '';
      nextDescriptions.en =
        enData.data.description || enData.data.description_en || '';
    }
    if (fiData?.data) {
      nextTitles.fi = fiData.data.title_fi || '';
      nextDescriptions.fi = fiData.data.description_fi || '';
    }
    if (deData?.data) {
      nextTitles.de = deData.data.title_de || '';
      nextDescriptions.de = deData.data.description_de || '';
    }
    if (svData?.data) {
      nextTitles.sv = svData.data.title_sv || '';
      nextDescriptions.sv = svData.data.description_sv || '';
    }

    setTitles(nextTitles);
    setDescriptions(nextDescriptions);
  }, [enData, fiData, deData, svData]);

  const [updateTemplate, { isLoading: isSaving } = { isLoading: false }] =
    useUpdateNotificationTemplateMutation();

  const handleSave = async () => {
    if (!id) return;
    try {
      const payload = {
        title: titles.en,
        description: descriptions.en,
        title_fi: titles.fi,
        description_fi: descriptions.fi,
        title_de: titles.de,
        description_de: descriptions.de,
        title_sv: titles.sv,
        description_sv: descriptions.sv,
      };
      await updateTemplate({ id, data: payload, lang: 'en' }).unwrap();
      setMode('view');
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              title: payload.title,
              description: payload.description,
              title_fi: payload.title_fi,
              description_fi: payload.description_fi,
              title_de: payload.title_de,
              description_de: payload.description_de,
              title_sv: payload.title_sv,
              description_sv: payload.description_sv,
              updated_at: new Date().toISOString(),
            }
          : prev
      );
    } catch (e) {
      console.error(e);
    }
  };

  const headerTitle = useMemo(() => {
    return mode === 'edit'
      ? 'Edit Notification Template'
      : 'Notification Template Details';
  }, [mode]);

  return (
    <>
      <Container>
        <Toolbar>
          <ToolbarHeading>
            <ToolbarPageTitle text={headerTitle} />
            <ToolbarDescription>{headerTitle}</ToolbarDescription>
          </ToolbarHeading>
          <ToolbarActions>
            {mode === 'view' ? (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setMode('edit')}
              >
                <KeenIcon type="filled" icon="pencil" /> Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => setMode('view')}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </ToolbarActions>
        </Toolbar>
      </Container>

      <Container>
        <div className="card">
          <div className="card-body space-y-10">
            {!template && (
              <div className="text-gray-600">Loading template content...</div>
            )}
            {!!template && (
              <>
                {LANGS.map((lng) => (
                  <div key={lng} className="space-y-4">
                    <h1 className="text-base font-semibold text-gray-800">
                      {lng.toUpperCase()} Content
                    </h1>
                    <div>
                      <label
                        htmlFor={`notification-title-${lng}`}
                        className="block text-sm font-medium mb-1"
                      >
                        Title ({lng.toUpperCase()})
                      </label>
                      <input
                        id={`notification-title-${lng}`}
                        type="text"
                        value={titles[lng]}
                        onChange={(e) =>
                          setTitles((s) => ({ ...s, [lng]: e.target.value }))
                        }
                        className="input input-md w-full"
                        disabled={mode === 'view'}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      {mode === 'view' ? (
                        <span
                          id={`notification-editor-${lng}-label`}
                          className="block text-sm font-medium mb-1"
                        >
                          Description HTML ({lng.toUpperCase()})
                        </span>
                      ) : (
                        <label
                          htmlFor={`notification-editor-${lng}`}
                          className="block text-sm font-medium mb-1"
                        >
                          Description HTML ({lng.toUpperCase()})
                        </label>
                      )}
                      {mode === 'view' ? (
                        <div
                          id={`notification-editor-${lng}`}
                          className="prose max-w-none border rounded-md p-3 bg-white"
                          dangerouslySetInnerHTML={{
                            __html: descriptions[lng] || '',
                          }}
                          aria-labelledby={`notification-editor-${lng}-label`}
                          role="text"
                        />
                      ) : (
                        <Editor
                          tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                          id={`notification-editor-${lng}`}
                          value={descriptions[lng]}
                          onEditorChange={(content: string) =>
                            setDescriptions((b) => ({ ...b, [lng]: content }))
                          }
                          aria-label={`Notification template description editor for ${lng.toUpperCase()} language`}
                          init={{
                            height: 450,
                            menubar: false,
                            statusbar: true,
                            plugins: [
                              'lists',
                              'link',
                              'autolink',
                              'table',
                              'paste',
                              'wordcount',
                              'preview',
                              'image',
                              'code',
                            ],
                            toolbar:
                              'undo redo | bold italic underline forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | table link preview',
                            content_style:
                              'body { font-family: Arial, sans-serif; font-size:14px }' +
                              ' .mce-content-body .mce-item-table pre .Y2IQFc { white-space: break-spaces !important; }' +
                              ' .mce-content-body pre { white-space: break-spaces; }',
                            paste_data_images: true,
                            branding: false,
                            aria_label: `Notification template description editor for ${lng.toUpperCase()} language`,
                            setup: (editor: {
                              on: (event: string, callback: () => void) => void;
                              getContainer: () => HTMLElement | null;
                            }) => {
                              editor.on('init', () => {
                                // Fix accessibility issues with TinyMCE elements
                                // Wait for statusbar to be rendered
                                setTimeout(() => {
                                  const container = editor.getContainer();
                                  if (container) {
                                    // Fix resize handle: add role to div with aria-label
                                    const resizeHandle =
                                      container.querySelector(
                                        '[data-mce-name="resize-handle"]'
                                      ) as HTMLElement;
                                    if (resizeHandle) {
                                      resizeHandle.setAttribute(
                                        'role',
                                        'button'
                                      );
                                    }

                                    // Fix statusbar navigation: add unique aria-label
                                    const statusbarNav =
                                      container.querySelector(
                                        '.tox-statusbar__path[role="navigation"]'
                                      ) as HTMLElement;
                                    if (statusbarNav) {
                                      statusbarNav.setAttribute(
                                        'aria-label',
                                        `Editor path navigation for notification template ${lng.toUpperCase()} language`
                                      );
                                    }
                                  }
                                }, 100);
                              });
                            },
                          }}
                        />
                      )}
                    </div>
                    <hr className="border-gray-200" />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </Container>
    </>
  );
};

export default NotificationTemplateDetails;
